import React, { createContext, useContext, useReducer, useCallback, useEffect } from 'react'

export type CompilerType = 'pdfLaTeX' | 'LaTeX' | 'XeLaTeX' | 'LuaLaTeX'
export type EditorTheme = 'scidraft' | 'default' | 'monokai' | 'solarized'
export type OverallTheme = 'light' | 'dark' | 'auto'
export type KeybindingType = 'default' | 'vim' | 'emacs' | 'sublime' | 'vscode'

export interface EditorSettings {
  // Compiler settings
  compiler: CompilerType
  
  // Editor appearance
  editorTheme: EditorTheme
  overallTheme: OverallTheme
  fontSize: number // in pixels
  fontFamily: string
  lineHeight: 'normal' | 'compact' | 'comfortable' | number
  
  // Editor behavior
  autoComplete: boolean
  autoCloseBrackets: boolean
  codeCheck: boolean
  equationPreview: boolean
  
  // Keyboard shortcuts
  keybindings: KeybindingType
  
  // Spell check
  spellCheck: string // Language code, e.g., 'en-US'
  
  // Autocomplete settings
  autocompleteActivateOnTyping: boolean
  autocompleteMinLength: number
  autocompleteMaxOptions: number
}

const defaultSettings: EditorSettings = {
  compiler: 'pdfLaTeX',
  editorTheme: 'scidraft',
  overallTheme: 'auto',
  fontSize: 14,
  fontFamily: 'Lucida Console, Monaco, "Courier New", monospace',
  lineHeight: 'normal',
  autoComplete: true,
  autoCloseBrackets: true,
  codeCheck: true,
  equationPreview: true,
  keybindings: 'default',
  spellCheck: 'en-US',
  autocompleteActivateOnTyping: true,
  autocompleteMinLength: 1,
  autocompleteMaxOptions: 50,
}

type EditorSettingsAction =
  | { type: 'SET_SETTINGS'; payload: Partial<EditorSettings> }
  | { type: 'RESET_SETTINGS' }
  | { type: 'LOAD_SETTINGS'; payload: EditorSettings }

interface EditorSettingsState {
  settings: EditorSettings
}

const initialState: EditorSettingsState = {
  settings: defaultSettings,
}

function editorSettingsReducer(
  state: EditorSettingsState,
  action: EditorSettingsAction
): EditorSettingsState {
  switch (action.type) {
    case 'SET_SETTINGS':
      const newSettings = { ...state.settings, ...action.payload }
      // Persist to localStorage
      try {
        localStorage.setItem('editorSettings', JSON.stringify(newSettings))
      } catch (error) {
        console.warn('Failed to save editor settings to localStorage:', error)
      }
      return { ...state, settings: newSettings }
    
    case 'RESET_SETTINGS':
      try {
        localStorage.removeItem('editorSettings')
      } catch (error) {
        console.warn('Failed to remove editor settings from localStorage:', error)
      }
      return { ...state, settings: defaultSettings }
    
    case 'LOAD_SETTINGS':
      return { ...state, settings: action.payload }
    
    default:
      return state
  }
}

interface EditorSettingsContextType {
  settings: EditorSettings
  updateSettings: (settings: Partial<EditorSettings>) => void
  resetSettings: () => void
}

const EditorSettingsContext = createContext<EditorSettingsContextType | undefined>(undefined)

export const EditorSettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(editorSettingsReducer, initialState)

  // Load settings from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem('editorSettings')
      if (saved) {
        const parsed = JSON.parse(saved)
        // Merge with defaults to handle new settings that might not exist in old saved data
        const merged = { ...defaultSettings, ...parsed }
        dispatch({ type: 'LOAD_SETTINGS', payload: merged })
      }
    } catch (error) {
      console.warn('Failed to load editor settings from localStorage:', error)
    }
  }, [])

  const updateSettings = useCallback((settings: Partial<EditorSettings>) => {
    dispatch({ type: 'SET_SETTINGS', payload: settings })
  }, [])

  const resetSettings = useCallback(() => {
    dispatch({ type: 'RESET_SETTINGS' })
  }, [])

  const value: EditorSettingsContextType = {
    settings: state.settings,
    updateSettings,
    resetSettings,
  }

  return (
    <EditorSettingsContext.Provider value={value}>
      {children}
    </EditorSettingsContext.Provider>
  )
}

export const useEditorSettings = () => {
  const context = useContext(EditorSettingsContext)
  if (context === undefined) {
    throw new Error('useEditorSettings must be used within an EditorSettingsProvider')
  }
  return context
}

